/**
 * Customizer Edit Parser
 * Parses cart line item properties back into Customizer V2 state structure
 */
(function() {
  'use strict';

  window.CustomizerEditParser = {
    /**
     * Parse cart item properties into customizer state
     * @param {Object} properties - Cart item properties object
     * @param {Array} availableLocations - Available locations from CustomizerV2Data
     * @param {Object} customizerData - Optional CustomizerV2Data for URL construction
     * @returns {Object} { selectedLocations, locationState }
     */
    parseCartProperties(properties, availableLocations, customizerData = null) {
      const result = {
        selectedLocations: [],
        locationState: {}
      };

      if (!properties) {
        return result;
      }

      // Convert array to object if needed
      // Shopify returns properties in various formats:
      // 1. Object: {"key": "value"}
      // 2. Array of arrays: [["key", "value"]]
      // 3. Array of objects: [{first: "key", last: "value"}]
      let propsObject = properties;
      if (Array.isArray(properties)) {
        propsObject = {};
        properties.forEach(item => {
          if (Array.isArray(item) && item.length >= 2) {
            // Format: [["key", "value"]]
            propsObject[item[0]] = item[1];
          } else if (item && typeof item === 'object') {
            // Format: [{first: "key", last: "value"}] or [{name: "key", value: "val"}]
            const key = item.first || item.name || item.key;
            const val = item.last || item.value;
            if (key !== undefined) {
              propsObject[key] = val;
            }
          }
        });
      }

      if (typeof propsObject !== 'object') {
        return result;
      }

      // Store customizerData for URL construction in parseLogo
      this._customizerData = customizerData;

      // Use the converted object for parsing
      properties = propsObject;

      // Find all locations (Location 1, Location 2, etc.)
      const locationCount = this.getLocationCount(properties);

      for (let i = 1; i <= locationCount; i++) {
        const locationData = this.parseLocation(properties, i, availableLocations);
        if (locationData) {
          result.selectedLocations.push(locationData.id);
          result.locationState[locationData.id] = locationData.state;
        }
      }

      // Clean up
      this._customizerData = null;

      return result;
    },

    /**
     * Get the number of locations in properties
     * @param {Object} properties
     * @returns {number}
     */
    getLocationCount(properties) {
      let count = 0;
      for (let i = 1; i <= 10; i++) {
        if (properties[`Location ${i}`]) {
          count = i;
        } else {
          break;
        }
      }
      return count;
    },

    /**
     * Parse a single location's data
     * @param {Object} properties
     * @param {number} index - 1-based location index
     * @param {Array} availableLocations - Available locations to match against
     * @returns {Object|null} { id, name, state }
     */
    parseLocation(properties, index, availableLocations) {
      const locationName = properties[`Location ${index}`];

      if (!locationName) return null;

      // Find matching location from available locations
      const matchedLocation = availableLocations?.find(loc =>
        loc.name.toLowerCase() === locationName.toLowerCase() ||
        loc.id.toLowerCase() === locationName.toLowerCase().replace(/\s+/g, '-')
      );

      const locationId = matchedLocation?.id || locationName.toLowerCase().replace(/\s+/g, '-');

      const logo = this.parseLogo(properties, index);

      return {
        id: locationId,
        name: locationName,
        state: {
          selectedLogo: logo,
          logoPosition: this.parseLogoPosition(properties, index),
          logoThreadColors: this.parseThreadColors(properties, index),
          svgThreadLayers: {}, // Will be reparsed from SVG when logo loads
          personalization: this.parsePersonalization(properties, index)
        }
      };
    },

    /**
     * Parse logo info from properties
     * @param {Object} properties
     * @param {number} index
     * @returns {Object|null} { id, url }
     */
    parseLogo(properties, index) {
      const logoId = properties[`Logo ${index}`];
      if (!logoId) return null;

      // Use customizerData if available for consistent URL construction
      const data = this._customizerData;
      const logoStorageUrl = data?.logoStorageUrl || window.LOGO_STORAGE_BASE_URL || '';

      return {
        id: logoId,
        url: logoStorageUrl ? `${logoStorageUrl}/${logoId}.svg` : ''
      };
    },

    /**
     * Parse logo position from coordinates string
     * Format: "left_top_scaleX*100_scaleY*100" (legacy)
     * Format: "left_top_scaleX*100_scaleY*100_canvasWidth_canvasHeight" (new)
     * @param {Object} properties
     * @param {number} index
     * @returns {Object|null} { left, top, scaleX, scaleY, angle, canvasWidth, canvasHeight }
     */
    parseLogoPosition(properties, index) {
      const coords = properties[`_logo${index}Coordinates`];
      if (!coords) return null;

      const parts = coords.split('_');
      if (parts.length < 4) return null;

      // Canvas dimensions: use from coordinates if available (parts 5 & 6)
      // Otherwise default to 600 (typical product page size)
      const canvasWidth = parts.length >= 6 ? parseInt(parts[4], 10) : 600;
      const canvasHeight = parts.length >= 6 ? parseInt(parts[5], 10) : 600;

      return {
        left: parseInt(parts[0], 10) || 0,
        top: parseInt(parts[1], 10) || 0,
        scaleX: (parseInt(parts[2], 10) || 100) / 100,
        scaleY: (parseInt(parts[3], 10) || 100) / 100,
        angle: parseFloat(properties[`_logo${index}RotateAngle`] || '0'),
        canvasWidth: canvasWidth || 600,
        canvasHeight: canvasHeight || 600
      };
    },

    /**
     * Parse thread colors from comma-separated strings
     * @param {Object} properties
     * @param {number} index
     * @returns {Object} { [logoId]: { [threadIndex]: { hex, name, code } } }
     */
    parseThreadColors(properties, index) {
      const logoId = properties[`Logo ${index}`];
      if (!logoId) return {};

      const names = (properties[`_logo${index}ThreadNames`] || '').split(',');
      const hexes = (properties[`_logo${index}ThreadColors`] || '').split(',');
      const codes = (properties[`_logo${index}ThreadCodes`] || '').split(',');

      const colors = {};
      colors[logoId] = {};

      names.forEach((name, i) => {
        if (name && name.trim()) {
          colors[logoId][i] = {
            name: name.trim(),
            hex: (hexes[i] || '#808080').trim(),
            code: (codes[i] || '').trim()
          };
        }
      });

      return colors;
    },

    /**
     * Parse personalization data
     * @param {Object} properties
     * @param {number} index
     * @returns {Object} personalization state
     */
    parsePersonalization(properties, index) {
      const text = properties[`Personalization ${index}`] || '';
      const fontName = properties[`Personalization ${index} Font`] || '';
      const fontSize = properties[`Personalization ${index} Font Size`] || '';
      const threadName = properties[`Personalization ${index} Thread`] || 'White';
      const threadHex = properties[`_personalization${index}ThreadColors`] || '#FFFFFF';
      const threadCode = properties[`_personalization${index}ThreadCodes`] || '001';
      const coords = properties[`_personalization${index}Coordinates`] || '';

      // Parse position from coordinates string
      // Legacy format: "left_top_fontSize_angle"
      // New format: "left_top_fontSize_angle_canvasWidth_canvasHeight"
      let position = null;
      if (coords) {
        const parts = coords.split('_');
        if (parts.length >= 3) {
          // Canvas dimensions: use from coordinates if available (parts 5 & 6)
          const canvasWidth = parts.length >= 6 ? parseInt(parts[4], 10) : 600;
          const canvasHeight = parts.length >= 6 ? parseInt(parts[5], 10) : 600;

          position = {
            left: parseInt(parts[0], 10) || 0,
            top: parseInt(parts[1], 10) || 0,
            fontSize: parseInt(parts[2], 10) || 40,
            angle: parseFloat(parts[3] || '0'),
            canvasWidth: canvasWidth || 600,
            canvasHeight: canvasHeight || 600
          };
        }
      }

      return {
        text: text,
        font: fontName ? { name: fontName, family: fontName } : null,
        textAlign: 'center', // Default, not stored in properties
        threadColor: {
          hex: threadHex,
          name: threadName,
          code: threadCode
        },
        position: position,
        enableTextResizing: true
      };
    },

    /**
     * Match font name to available fonts
     * @param {string} fontName - Saved font name
     * @param {Array} availableFonts - Fonts from CustomizerV2Data.personalizationFonts
     * @returns {Object|null} Matched font object
     */
    matchFont(fontName, availableFonts) {
      if (!fontName || !availableFonts || !availableFonts.length) {
        return null;
      }

      // Try exact match first
      const exactMatch = availableFonts.find(f =>
        f.name.toLowerCase() === fontName.toLowerCase()
      );
      if (exactMatch) return exactMatch;

      // Try partial match
      const partialMatch = availableFonts.find(f =>
        f.name.toLowerCase().includes(fontName.toLowerCase()) ||
        fontName.toLowerCase().includes(f.name.toLowerCase())
      );
      if (partialMatch) return partialMatch;

      // Return first font as fallback
      return availableFonts[0];
    },

    /**
     * Enhance parsed state with matched fonts from available fonts
     * @param {Object} parsedState - Result from parseCartProperties
     * @param {Array} availableFonts - Fonts from CustomizerV2Data.personalizationFonts
     * @returns {Object} Enhanced state with proper font objects
     */
    enhanceWithFonts(parsedState, availableFonts) {
      if (!availableFonts || !availableFonts.length) {
        return parsedState;
      }

      Object.keys(parsedState.locationState).forEach(locationId => {
        const locState = parsedState.locationState[locationId];
        if (locState.personalization && locState.personalization.font) {
          const matchedFont = this.matchFont(
            locState.personalization.font.name,
            availableFonts
          );
          if (matchedFont) {
            locState.personalization.font = matchedFont;
          }
        }
      });

      return parsedState;
    },

    // ============================================
    // ORDER ATTRIBUTE PARSING (B2B Order Editing)
    // ============================================

    /**
     * Parse order attribute customizations JSON
     * Used for B2B order editing where data is stored in order/cart attributes
     * @param {Object|string} customizationsAttr - _customizations attribute value
     * @param {string} variantId - Variant ID to find
     * @param {number} itemIndex - Item index (0, 1, 2...)
     * @param {Object} customizerData - Optional CustomizerV2Data for URL construction
     * @returns {Object|null} { selectedLocations, locationState } or null
     */
    parseOrderAttributeCustomizations(customizationsAttr, variantId, itemIndex = 0, customizerData = null) {
      if (!customizationsAttr) {
        return null;
      }

      try {
        const customizations = typeof customizationsAttr === 'string' ?
          JSON.parse(customizationsAttr) : customizationsAttr;

        const key = `variant_${variantId}_${itemIndex}`;
        const itemData = customizations[key];

        if (!itemData) {
          // Try to find any matching variant key (in case index is wrong)
          const matchingKey = Object.keys(customizations).find(k => k.startsWith(`variant_${variantId}_`));
          if (matchingKey) {
            return this.convertOrderAttrToState(customizations[matchingKey], customizerData);
          }
          return null;
        }

        return this.convertOrderAttrToState(itemData, customizerData);
      } catch (e) {
        return null;
      }
    },

    /**
     * Convert order attribute format to customizer state format
     * @param {Object} itemData - Item customization data from order attributes
     * @param {Object} customizerData - Optional CustomizerV2Data for URL construction
     * @returns {Object} { selectedLocations, locationState }
     */
    convertOrderAttrToState(itemData, customizerData = null) {
      const result = {
        selectedLocations: [],
        locationState: {}
      };

      if (!itemData?.locations || !Array.isArray(itemData.locations)) {
        return result;
      }

      // Get URL construction info
      const logoStorageUrl = customizerData?.logoStorageUrl || window.LOGO_STORAGE_BASE_URL || '';

      itemData.locations.forEach(loc => {
        if (!loc.id) {
          return;
        }

        result.selectedLocations.push(loc.id);

        const locState = {
          selectedLogo: null,
          logoPosition: null,
          logoThreadColors: {},
          svgThreadLayers: {}, // Will be reparsed from SVG when logo loads
          personalization: null,
          noLogo: loc.noLogo || false // User opted out of logo for this location
        };

        // Parse logo
        if (loc.logo && loc.logo.id) {
          // Construct URL if not provided
          const logoUrl = loc.logo.url || `${logoStorageUrl}/${loc.logo.id}.svg`;

          locState.selectedLogo = {
            id: loc.logo.id,
            url: logoUrl
          };

          // Parse logo position from coordinates string
          if (loc.logo.coordinates) {
            const parts = loc.logo.coordinates.split('_');
            if (parts.length >= 4) {
              locState.logoPosition = {
                left: parseInt(parts[0], 10) || 0,
                top: parseInt(parts[1], 10) || 0,
                scaleX: (parseInt(parts[2], 10) || 100) / 100,
                scaleY: (parseInt(parts[3], 10) || 100) / 100,
                canvasWidth: parts.length >= 6 ? parseInt(parts[4], 10) : 600,
                canvasHeight: parts.length >= 6 ? parseInt(parts[5], 10) : 600,
                angle: loc.logo.rotateAngle || 0
              };
            }
          }

          // Parse thread colors
          if (loc.logo.threads && loc.logo.threads.length > 0) {
            locState.logoThreadColors[loc.logo.id] = {};
            loc.logo.threads.forEach((thread, i) => {
              if (thread.name && thread.name.trim()) {
                locState.logoThreadColors[loc.logo.id][i] = {
                  name: thread.name.trim(),
                  hex: (thread.hex || '#808080').trim(),
                  code: (thread.code || '').trim()
                };
              }
            });
          }
        }

        // Parse personalization
        if (loc.personalization && loc.personalization.text) {
          const p = loc.personalization;
          let position = null;

          // Parse position from coordinates string
          if (p.coordinates) {
            const parts = p.coordinates.split('_');
            if (parts.length >= 3) {
              position = {
                left: parseInt(parts[0], 10) || 0,
                top: parseInt(parts[1], 10) || 0,
                fontSize: parseInt(parts[2], 10) || p.fontSize || 40,
                angle: parseFloat(parts[3] || '0'),
                canvasWidth: parts.length >= 6 ? parseInt(parts[4], 10) : 600,
                canvasHeight: parts.length >= 6 ? parseInt(parts[5], 10) : 600
              };
            }
          }

          locState.personalization = {
            text: p.text,
            font: p.font ? {
              name: p.font,
              family: p.fontFamily || p.font
            } : null,
            textAlign: p.textAlign || 'center',
            threadColor: {
              hex: p.threadColor || '#FFFFFF',
              name: p.threadName || 'White',
              code: p.threadCode || '001'
            },
            position: position,
            enableTextResizing: true
          };
        }

        result.locationState[loc.id] = locState;
      });

      return result;
    },

    /**
     * Get all customization keys for a specific variant from order attributes
     * @param {Object|string} customizationsAttr - _customizations attribute value
     * @param {string} variantId - Variant ID to find
     * @returns {Array<string>} Array of matching keys
     */
    getCustomizationKeysForVariant(customizationsAttr, variantId) {
      if (!customizationsAttr) return [];

      try {
        const customizations = typeof customizationsAttr === 'string' ?
          JSON.parse(customizationsAttr) : customizationsAttr;

        return Object.keys(customizations).filter(k => k.startsWith(`variant_${variantId}_`));
      } catch (e) {
        return [];
      }
    }
  };
})();